import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Settings as SettingsIcon, 
  Key, 
  Sliders, 
  Save,
  Loader2,
  Eye,
  EyeOff,
  Youtube,
  Check,
  Users,
  Shield,
  Lock,
  UserPlus,
  Trash2,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import * as api from '@/lib/api';
import { AccountCover, AccountStatusBadge } from '@/components/AccountCover';
import { useAuth } from '@/lib/AuthContext';
import { useLanguage } from '@/lib/LanguageContext';

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    clip_min_duration: 15,
    clip_max_duration: 60,
    clips_per_video: 5,
    auto_discover: true,
    auto_publish: false,
    virality_threshold: 0.7,
    subtitle_style: 'bold_animated',
    discovery_keywords: ['podcast', 'interview', 'conversation']
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({});

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState({});

  // Guest management state (developer only)
  const [guests, setGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [newGuest, setNewGuest] = useState({
    email: '',
    durationDays: 7,
    plan: 'pro',
    note: ''
  });
  const [addingGuest, setAddingGuest] = useState(false);
  const [adminStats, setAdminStats] = useState(null);

  const [platformSettings, setPlatformSettings] = useState({
    youtube: { enabled: false, api_key: '', auto_publish: false },
    tiktok: { enabled: false, api_key: '', auto_publish: false },
    instagram: { enabled: false, api_key: '', auto_publish: false },
    bilibili: { enabled: false, api_key: '', auto_publish: false }
  });
  const navigate = useNavigate();

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.getSettings();
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        if (response.data.platforms) {
          const platforms = {};
          response.data.platforms.forEach(p => {
            platforms[p.platform] = p;
          });
          setPlatformSettings(prev => ({ ...prev, ...platforms }));
        }
      }
    } catch (error) {
      logger.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGuests = useCallback(async () => {
    setLoadingGuests(true);
    try {
      const response = await api.getGuestList();
      setGuests(response.data.guests || []);
    } catch (error) {
      logger.error('Failed to load guests:', error);
    } finally {
      setLoadingGuests(false);
    }
  }, []);

  const fetchAdminStats = useCallback(async () => {
    try {
      const response = await api.getAdminStats();
      setAdminStats(response.data);
    } catch (error) {
      logger.error('Failed to load admin stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    if (user?.is_developer) {
      fetchGuests();
      fetchAdminStats();
    }
  }, [user, fetchSettings, fetchGuests, fetchAdminStats]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.updateSettings(settings);
      toast.success(t('settings.settingsSaved'));
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchConnectedAccounts = useCallback(async () => {
    try {
      const response = await api.getConnectedAccounts();
      const accounts = response.data.accounts || [];
      
      // Group accounts by platform
      const grouped = { youtube: [], tiktok: [], instagram: [], bilibili: [] };
      accounts.forEach(acc => {
        if (grouped[acc.platform]) {
          grouped[acc.platform].push({
            id: acc.id,
            username: acc.platform_username,
            display_name: acc.platform_display_name,
            status: acc.status,
            cover_image: acc.cover_image,
            raw: acc
          });
        }
      });
      
      // Update platform settings with connected accounts
      setPlatformSettings(prev => ({
        youtube: { ...prev.youtube, enabled: grouped.youtube.length > 0, accounts: grouped.youtube },
        tiktok: { ...prev.tiktok, enabled: grouped.tiktok.length > 0, accounts: grouped.tiktok },
        instagram: { ...prev.instagram, enabled: grouped.instagram.length > 0, accounts: grouped.instagram }
      }));
    } catch (error) {
      logger.error('Failed to fetch connected accounts:', error);
    }
  }, []);

  useEffect(() => {
    fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  const handleSavePlatform = async (platform) => {
    setSaving(true);
    try {
      await api.updatePlatformSettings(platform, {
        platform,
        ...platformSettings[platform]
      });
      toast.success(`${platform} settings saved`);
    } catch (error) {
      toast.error('Failed to save platform settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('account.passwordMismatch'));
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success(t('account.passwordChanged'));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('account.passwordChangeFailed'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    setAddingGuest(true);
    try {
      await api.createGuestAccess(
        newGuest.email,
        newGuest.durationDays,
        newGuest.plan,
        newGuest.note
      );
      toast.success(`Acesso concedido para ${newGuest.email}`);
      setGuestDialogOpen(false);
      setNewGuest({ email: '', durationDays: 7, plan: 'pro', note: '' });
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao adicionar convidado');
    } finally {
      setAddingGuest(false);
    }
  };

  const handleRevokeGuest = async (guestId) => {
    try {
      await api.revokeGuestAccess(guestId);
      toast.success('Acesso revogado');
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao revogar acesso');
    }
  };

  const toggleShowKey = (platform) => {
    setShowKeys(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold">{t('settings.title')}</h1>
        <p className="text-muted-foreground">{t('settings.subtitle')}</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-muted/50 border border-border flex-wrap">
          <TabsTrigger value="general">
            <Sliders className="w-4 h-4 mr-2" />
            {t('settings.general')}
          </TabsTrigger>
          <TabsTrigger value="account">
            <Lock className="w-4 h-4 mr-2" />
            {t('account.title') || 'Account'}
          </TabsTrigger>
          <TabsTrigger value="platforms">
            <Youtube className="w-4 h-4 mr-2" />
            {t('settings.platforms')}
          </TabsTrigger>
          {user?.is_developer && (
            <TabsTrigger value="admin">
              <Shield className="w-4 h-4 mr-2" />
              Admin
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="mt-6 space-y-6">
          {/* Clip Generation */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('settings.clipGeneration')}</CardTitle>
              <CardDescription>{t('settings.clipGenerationDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>{t('settings.minDuration')}</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.clip_min_duration]}
                      onValueChange={([value]) => setSettings(s => ({ ...s, clip_min_duration: value }))}
                      min={10}
                      max={30}
                      step={5}
                      className="flex-1"
                      data-testid="min-duration-slider"
                    />
                    <span className="w-12 text-right font-mono">{settings.clip_min_duration}s</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.maxDuration')}</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.clip_max_duration]}
                      onValueChange={([value]) => setSettings(s => ({ ...s, clip_max_duration: value }))}
                      min={30}
                      max={90}
                      step={5}
                      className="flex-1"
                      data-testid="max-duration-slider"
                    />
                    <span className="w-12 text-right font-mono">{settings.clip_max_duration}s</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('settings.clipsPerVideo')}</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[settings.clips_per_video]}
                    onValueChange={([value]) => setSettings(s => ({ ...s, clips_per_video: value }))}
                    min={1}
                    max={10}
                    step={1}
                    className="flex-1 max-w-md"
                    data-testid="clips-per-video-slider"
                  />
                  <span className="w-12 text-right font-mono">{settings.clips_per_video}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('settings.viralityThreshold')}</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[settings.virality_threshold * 100]}
                    onValueChange={([value]) => setSettings(s => ({ ...s, virality_threshold: value / 100 }))}
                    min={50}
                    max={95}
                    step={5}
                    className="flex-1 max-w-md"
                    data-testid="virality-threshold-slider"
                  />
                  <span className="w-12 text-right font-mono">{(settings.virality_threshold * 100).toFixed(0)}%</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('settings.viralityThresholdDesc')}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t('settings.subtitleStyle')}</Label>
                <Select 
                  value={settings.subtitle_style} 
                  onValueChange={(value) => setSettings(s => ({ ...s, subtitle_style: value }))}
                >
                  <SelectTrigger className="max-w-md" data-testid="subtitle-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bold_animated">{t('settings.boldAnimated')}</SelectItem>
                    <SelectItem value="minimal">{t('settings.minimal')}</SelectItem>
                    <SelectItem value="karaoke">{t('settings.karaokeStyle')}</SelectItem>
                    <SelectItem value="cinematic">{t('settings.cinematic')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Automation */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('settings.automation')}</CardTitle>
              <CardDescription>{t('settings.automationDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('settings.autoDiscovery')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.autoDiscoveryDesc')}
                  </p>
                </div>
                <Switch
                  checked={settings.auto_discover}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, auto_discover: checked }))}
                  data-testid="auto-discover-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('settings.autoPublish')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.autoPublishDesc')}
                  </p>
                </div>
                <Switch
                  checked={settings.auto_publish}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, auto_publish: checked }))}
                  data-testid="auto-publish-switch"
                />
              </div>
            </CardContent>
          </Card>

          <Button 
            onClick={handleSaveSettings} 
            disabled={saving}
            className="w-full sm:w-auto"
            data-testid="save-general-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('settings.saveSettings')}
          </Button>
        </TabsContent>

        {/* Account Settings */}
        <TabsContent value="account" className="mt-6 space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('account.changePassword')}</CardTitle>
              <CardDescription>{t('account.changePasswordDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">{t('account.currentPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showPasswordFields.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))}
                      required
                      data-testid="current-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowPasswordFields(s => ({ ...s, current: !s.current }))}
                    >
                      {showPasswordFields.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('account.newPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPasswordFields.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(p => ({ ...p, newPassword: e.target.value }))}
                      required
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowPasswordFields(s => ({ ...s, new: !s.new }))}
                    >
                      {showPasswordFields.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('account.confirmPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showPasswordFields.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
                      required
                      data-testid="confirm-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowPasswordFields(s => ({ ...s, confirm: !s.confirm }))}
                    >
                      {showPasswordFields.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={changingPassword}
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  {t('account.changePassword')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{t('account.accountInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">{t('account.plan')}</span>
                <Badge variant={user?.plan === 'developer' ? 'default' : 'secondary'}>
                  {user?.plan?.toUpperCase()}
                </Badge>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-muted-foreground">{t('account.emailVerified')}</span>
                {user?.is_email_verified ? (
                  <Badge variant="default" className="bg-green-600">
                    <Check className="w-3 h-3 mr-1" />
                    {t('account.verified')}
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {t('account.notVerified')}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Settings */}
        <TabsContent value="platforms" className="mt-6 space-y-6">
          {/* Account Limits Info */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="font-medium">Limites do seu Plano ({user?.plan?.toUpperCase()})</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {user?.plan === 'developer' || user?.plan === 'enterprise' 
                    ? '50 contas por plataforma (150 total)'
                    : user?.plan === 'pro'
                    ? '5 contas por plataforma (15 total)'
                    : '1 conta por plataforma (3 total)'}
                </div>
              </div>
            </CardContent>
          </Card>

          {['youtube', 'tiktok', 'instagram', 'bilibili'].map((platform) => {
            const labels = {
              youtube: { name: 'YouTube Shorts', icon: '▶️', format: 'Shorts (vertical 9:16, até 60s)' },
              tiktok: { name: 'TikTok', icon: '🎵', format: 'Feed (vertical 9:16, até 180s)' },
              instagram: { name: 'Instagram Reels', icon: '📸', format: 'Reels (vertical 9:16, até 90s)' },
              bilibili: { name: 'Bilibili', icon: '📺', format: 'Vertical 9:16, até 300s' }
            };

            return (
              <Card key={platform} className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{labels[platform].icon}</span>
                      <div>
                        <CardTitle className="text-lg font-heading">{labels[platform].name}</CardTitle>
                        <CardDescription>
                          Formato: {labels[platform].format}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant={platformSettings[platform]?.accounts?.length ? 'default' : 'secondary'}>
                      {platformSettings[platform]?.accounts?.length ? `${platformSettings[platform].accounts.length} conta(s) no Cofre` : 'Sem contas'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Connected Accounts */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Contas Conectadas
                    </Label>
                    
                    {platformSettings[platform]?.accounts?.length > 0 ? (
                      <div className="space-y-2">
                        {platformSettings[platform].accounts.map((account) => (
                          <div 
                            key={account.id || account.username}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div>
                                <p className="font-medium text-sm">{account.display_name || account.username}</p>
                                <p className="text-xs text-muted-foreground">{account.username}</p>
                                <div className="mt-1"><AccountStatusBadge status={account.status} /></div>
                              </div>
                            </div>
                            <AccountCover credential={account.raw} onChanged={fetchConnectedAccounts} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">
                        Nenhuma conta conectada
                      </p>
                    )}
                  </div>

                  {/* Accounts are added in the Secure Vault and operated by the Desktop Agent */}
                  <div className="pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/vault')}
                      data-testid={`connect-${platform}-btn`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Adicionar conta no Cofre Seguro
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Sem OAuth ou APIs: o Agente Desktop faz login e opera a conta manualmente
                    </p>
                  </div>

                  {/* Auto Publish Toggle */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <div className="space-y-0.5">
                      <Label>Auto Publicar em {labels[platform].name}</Label>
                      <p className="text-xs text-muted-foreground">
                        Publicar clips automaticamente quando prontos
                      </p>
                    </div>
                    <Switch
                      checked={platformSettings[platform]?.auto_publish}
                      onCheckedChange={(checked) => 
                        setPlatformSettings(s => ({
                          ...s,
                          [platform]: { ...s[platform], auto_publish: checked }
                        }))
                      }
                      data-testid={`${platform}-auto-publish-switch`}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Admin/Developer Settings */}
        {user?.is_developer && (
          <TabsContent value="admin" className="mt-6 space-y-6">
            {/* Admin Stats */}
            {adminStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-card border-border">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{adminStats.total_users}</div>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{adminStats.verified_users}</div>
                    <p className="text-xs text-muted-foreground">Verified</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{adminStats.total_clips}</div>
                    <p className="text-xs text-muted-foreground">Total Clips</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{adminStats.active_guests}</div>
                    <p className="text-xs text-muted-foreground">Active Guests</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Guest Management */}
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Gerenciamento de Convidados
                    </CardTitle>
                    <CardDescription>
                      Conceda acesso temporário gratuito a usuários
                    </CardDescription>
                  </div>
                  <Dialog open={guestDialogOpen} onOpenChange={setGuestDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-guest-btn">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Adicionar Convidado
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Adicionar Novo Convidado</DialogTitle>
                        <DialogDescription>
                          Conceda acesso temporário a um usuário
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddGuest} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="guestEmail">Email</Label>
                          <Input
                            id="guestEmail"
                            type="email"
                            placeholder="email@example.com"
                            value={newGuest.email}
                            onChange={(e) => setNewGuest(g => ({ ...g, email: e.target.value }))}
                            required
                            data-testid="guest-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="guestPlan">Plano</Label>
                          <Select 
                            value={newGuest.plan} 
                            onValueChange={(value) => setNewGuest(g => ({ ...g, plan: value }))}
                          >
                            <SelectTrigger data-testid="guest-plan-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="individual">Individual</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem>
                              <SelectItem value="enterprise">Enterprise</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="guestDuration">Duração (dias)</Label>
                          <Select 
                            value={String(newGuest.durationDays)} 
                            onValueChange={(value) => setNewGuest(g => ({ ...g, durationDays: parseInt(value) }))}
                          >
                            <SelectTrigger data-testid="guest-duration-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">7 dias</SelectItem>
                              <SelectItem value="14">14 dias</SelectItem>
                              <SelectItem value="30">30 dias</SelectItem>
                              <SelectItem value="90">90 dias</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="guestNote">Nota (opcional)</Label>
                          <Input
                            id="guestNote"
                            placeholder="Motivo do acesso..."
                            value={newGuest.note}
                            onChange={(e) => setNewGuest(g => ({ ...g, note: e.target.value }))}
                            data-testid="guest-note-input"
                          />
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={addingGuest} data-testid="confirm-add-guest-btn">
                            {addingGuest ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : null}
                            Conceder Acesso
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {loadingGuests ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : guests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum convidado adicionado</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {guests.map((guest) => (
                      <div 
                        key={guest.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          guest.is_active ? 'border-border bg-muted/30' : 'border-destructive/30 bg-destructive/5'
                        }`}
                        data-testid={`guest-item-${guest.id}`}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{guest.email}</span>
                            <Badge variant={guest.is_active ? 'default' : 'destructive'}>
                              {guest.plan.toUpperCase()}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Expira: {formatDate(guest.expires_at)}
                            </span>
                            {guest.note && (
                              <span className="truncate max-w-[200px]">{guest.note}</span>
                            )}
                          </div>
                        </div>
                        {guest.is_active && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleRevokeGuest(guest.id)}
                            data-testid={`revoke-guest-${guest.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
